/* Crane-delivery access questions (D10).
 *
 * The questions work without this file: each control carries form="<product form id>" and
 * `required`, so the browser blocks add-to-cart natively and Horizon's
 * `new FormData(form)` picks the answers up as line item properties. Everything here is
 * enhancement or safety.
 *
 *   1. Wallet suppression. Shop Pay and friends start their own checkout for the variant
 *      and would skip the questions, so they are hidden while a gate is active.
 *   2. Machine summary. Collects each question's short code into one `_access` property
 *      (`overhead=Yes|truck=No|dist=8`), so future automation keys on stable codes rather
 *      than on English labels Per may reword. Leading underscore keeps it out of the
 *      customer's cart view while leaving it on the order.
 *   3. Binding repair, and fail safe: if no form can be found, add-to-cart is disabled and
 *      the customer is asked to phone, rather than silently ordering a 800 kg item with no
 *      access information.
 *   4. Danish validation messages, and dropping the name of empty optional fields so a
 *      blank answer never becomes an empty property on the order.
 */
class SgAccessGate extends HTMLElement {
  connectedCallback() {
    // Horizon re-renders sections on variant change; guard against double binding.
    if (this.dataset.sgBound === 'true') return;
    this.dataset.sgBound = 'true';

    this.fields = Array.from(this.querySelectorAll('.sg-aq-input'));
    if (!this.fields.length) return;

    const inactive = this.dataset.inactive === 'true';
    const form = this.findForm();

    if (!form) {
      this.failSafe();
      return;
    }

    // NEVER use form.id here. Horizon's product form contains <input name="id">, and a
    // form exposes its named controls as properties, so `form.id` returns that element
    // rather than the id string. Writing it back as a form attribute silently detaches
    // every field. Always go through getAttribute/setAttribute.
    let formId = form.getAttribute('id');
    if (!formId) {
      formId = this.dataset.formId || 'sg-product-form';
      form.setAttribute('id', formId);
    }

    if (!inactive) document.body.classList.add('sg-gate-active');

    this.summary = this.buildSummaryField(formId, inactive);

    this.fields.forEach((el) => {
      if (el.getAttribute('form') !== formId) el.setAttribute('form', formId);

      // In the theme editor on a product without the checkbox, do not block the merchant.
      if (inactive) el.removeAttribute('required');

      if (el.required) {
        const msg = 'Udfyld feltet, så chaufføren kan komme frem.';
        el.addEventListener('invalid', () => el.setCustomValidity(msg));
        el.addEventListener('input', () => el.setCustomValidity(''));
        el.addEventListener('change', () => el.setCustomValidity(''));
      }

      // Keep the real name aside, so an empty optional field submits nothing at all.
      el.dataset.sgName = el.getAttribute('name') || '';
      const sync = () => {
        if (el.value.trim() === '') el.removeAttribute('name');
        else el.setAttribute('name', el.dataset.sgName);
        this.updateSummary();
      };
      el.addEventListener('input', sync);
      el.addEventListener('change', sync);
      sync();
    });
  }

  disconnectedCallback() {
    // Do not let the wallet suppression leak onto the next product if the theme swaps
    // sections client-side rather than doing a full page load.
    if (!document.querySelector('sg-access-gate:not([data-inactive])')) {
      document.body.classList.remove('sg-gate-active');
    }
  }

  /** Hidden, form-associated field carrying the compact code=value summary. */
  buildSummaryField(formId, inactive) {
    if (inactive) return null;
    if (!this.fields.some((el) => el.dataset.sgCode)) return null;
    const el = document.createElement('input');
    el.type = 'hidden';
    el.setAttribute('form', formId);
    el.dataset.sgName = 'properties[_access]';
    this.appendChild(el);
    return el;
  }

  updateSummary() {
    if (!this.summary) return;
    const parts = this.fields
      .filter((el) => el.dataset.sgCode && el.value.trim() !== '')
      // Pipe and equals are the separators, so they cannot appear inside a value.
      .map((el) => `${el.dataset.sgCode}=${el.value.trim().replace(/[|=]/g, ' ')}`);
    if (parts.length) {
      this.summary.value = parts.join('|');
      this.summary.setAttribute('name', this.summary.dataset.sgName);
    } else {
      this.summary.removeAttribute('name');
    }
  }

  /** The id is the fast path; the data attribute is Horizon's own and outlives its ids. */
  findForm() {
    const byId = this.dataset.formId && document.getElementById(this.dataset.formId);
    if (byId) return byId;
    const scope = this.closest('.shopify-section') || document;
    return (
      scope.querySelector('form[data-type="add-to-cart-form"]') ||
      document.querySelector('form[data-type="add-to-cart-form"]')
    );
  }

  failSafe() {
    const notice = this.querySelector('[data-sg-fallback]');
    if (notice) notice.hidden = false;
    document
      .querySelectorAll('button[name="add"], add-to-cart-component button')
      .forEach((b) => {
        b.disabled = true;
        b.setAttribute('aria-disabled', 'true');
      });
  }
}

if (!customElements.get('sg-access-gate')) {
  customElements.define('sg-access-gate', SgAccessGate);
}
